import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Loader2, Sparkles, Eye, EyeOff, Mail, Shield, X, Gift } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { ScribPostLogo } from "@/components/scribpost-logo";
import { apiFetch } from "@/lib/api-fetch";

const FEATURES = [
  "Unlimited post generation",
  "All 5 platforms",
  "Voice profile saved forever",
  "All creator tools",
  "Priority AI generation",
];

export function usePaywallCheck() {
  const { postsGeneratedCount, isSubscribed } = useAppStore();
  const [showPaywall, setShowPaywall] = useState(false);

  const checkPaywall = useCallback((): boolean => {
    if (isSubscribed) return false;
    if (postsGeneratedCount >= 15) {
      setShowPaywall(true);
      return true;
    }
    return false;
  }, [postsGeneratedCount, isSubscribed]);

  const forceShowPaywall = useCallback(() => {
    setShowPaywall(true);
  }, []);

  const dismissPaywall = useCallback(() => {
    setShowPaywall(false);
  }, []);

  return { showPaywall, checkPaywall, forceShowPaywall, dismissPaywall };
}

type ModalStep = "signup" | "login" | "plans";
type BillingInterval = "monthly" | "yearly";
type AuthSuccessPayload = { isSubscribed: boolean };

function InlineSignupForm({ onSuccess }: { onSuccess: (payload: AuthSuccessPayload) => void }) {
  const { sessionId, displayName, loginUser, setCapturedEmail } = useAppStore();
  const capturedEmail = useAppStore((s) => s.capturedEmail);
  const { toast } = useToast();

  const [email, setEmail] = useState(capturedEmail ?? "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      setCapturedEmail(email.trim());
      const res = await apiFetch(`${baseUrl}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, name: displayName, sessionId, username: username.trim() }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Signup failed");
        return;
      }

      loginUser({
        token: data.token,
        userId: data.user.id,
        email: data.user.email,
        emailVerified: data.user.emailVerified,
        canonicalSessionId: data.user.canonicalSessionId,
        displayName: data.user.displayName,
        subscriptionStatus: data.user.subscriptionStatus ?? "none",
        subscriptionPlan: data.user.subscriptionPlan ?? null,
      });

      toast({
        title: "Account created!",
        description: "Check your email to verify your account.",
      });

      const subStatus = data.user.subscriptionStatus ?? "none";
      onSuccess({ isSubscribed: subStatus === "active" || subStatus === "trialing" });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      <div className="text-center mb-2">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <h3 className="font-display font-bold text-lg text-foreground">Create your account</h3>
        <p className="text-xs text-muted-foreground mt-1">Sign up to unlock ScribPost Pro</p>
      </div>

      <form onSubmit={handleSignup} className="space-y-3">
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl p-3"
          >
            {error}
          </motion.p>
        )}

        <Input
          type="email"
          placeholder="Your email"
          aria-label="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 rounded-xl bg-background border-border"
          autoComplete="email"
          required
          autoFocus
        />

        <Input
          type="text"
          placeholder="Choose a username (4+ chars)"
          aria-label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="h-11 rounded-xl bg-background border-border"
          autoComplete="username"
          minLength={4}
          maxLength={30}
          pattern="[a-zA-Z0-9_]+"
          title="Letters, numbers, and underscores only"
          required
        />

        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Create a password (8+ chars)"
            aria-label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 rounded-xl bg-background border-border pr-10"
            autoComplete="new-password"
            minLength={8}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <Button
          type="submit"
          className="w-full h-11 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
          disabled={loading || !email.trim() || password.length < 8}
          aria-busy={loading}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Create account & continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </form>
    </motion.div>
  );
}

function InlineLoginForm({ onSuccess }: { onSuccess: (payload: AuthSuccessPayload) => void }) {
  const { loginUser, sessionId } = useAppStore();
  const capturedEmail = useAppStore((s) => s.capturedEmail);
  const { toast } = useToast();

  const [email, setEmail] = useState(capturedEmail ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, anonymousSessionId: sessionId }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Login failed");
        return;
      }

      loginUser({
        token: data.token,
        userId: data.user.id,
        email: data.user.email,
        emailVerified: data.user.emailVerified,
        canonicalSessionId: data.user.canonicalSessionId,
        displayName: data.user.displayName,
        subscriptionStatus: data.user.subscriptionStatus ?? "none",
        subscriptionPlan: data.user.subscriptionPlan ?? null,
      });

      toast({ title: "Welcome back!", description: `Logged in as ${data.user.email}` });
      const subStatus = data.user.subscriptionStatus ?? "none";
      onSuccess({ isSubscribed: subStatus === "active" || subStatus === "trialing" });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      <div className="text-center mb-2">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Shield className="w-5 h-5 text-primary" />
        </div>
        <h3 className="font-display font-bold text-lg text-foreground">Sign in to ScribPost</h3>
        <p className="text-xs text-muted-foreground mt-1">Already have an account? Sign in to continue</p>
      </div>

      <form onSubmit={handleLogin} className="space-y-3">
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl p-3"
          >
            {error}
          </motion.p>
        )}

        <Input
          type="text"
          placeholder="Email or username"
          aria-label="Email or username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 rounded-xl bg-background border-border"
          autoComplete="username"
          required
          autoFocus
        />

        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Your password"
            aria-label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 rounded-xl bg-background border-border pr-10"
            autoComplete="current-password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <Button
          type="submit"
          className="w-full h-11 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
          disabled={loading || !email.trim() || !password}
          aria-busy={loading}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Log in & continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </form>
    </motion.div>
  );
}

function BillingToggle({
  selected,
  onChange,
}: {
  selected: BillingInterval;
  onChange: (v: BillingInterval) => void;
}) {
  return (
    <div className="flex items-center bg-secondary/40 border border-border/50 rounded-xl p-1 mb-5 gap-1">
      <button
        onClick={() => onChange("monthly")}
        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
          selected === "monthly"
            ? "text-primary-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        }`}
        style={
          selected === "monthly"
            ? { background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }
            : {}
        }
      >
        Monthly
      </button>
      <button
        onClick={() => onChange("yearly")}
        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-1.5 ${
          selected === "yearly"
            ? "text-primary-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        }`}
        style={
          selected === "yearly"
            ? { background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }
            : {}
        }
      >
        Annual
        <span
          className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
            selected === "yearly"
              ? "bg-white/20 text-white"
              : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
          }`}
        >
          -20%
        </span>
      </button>
    </div>
  );
}

function PlansStep({
  loading,
  onCheckout,
  hard,
  selectedPlan,
  onPlanChange,
}: {
  loading: boolean;
  onCheckout: () => void;
  hard: boolean;
  selectedPlan: BillingInterval;
  onPlanChange: (v: BillingInterval) => void;
}) {
  const isAnnual = selectedPlan === "yearly";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <div className="text-center mb-5">
        {hard ? (
          <>
            <h2 className="text-2xl font-display font-bold text-foreground">
              You've used all 15 free actions
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Upgrade to Pro and keep creating in your voice — unlimited, forever.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-display font-bold text-foreground">
              You've seen what ScribPost can do.
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Upgrade to Pro and keep creating in your voice — unlimited, forever.
            </p>
          </>
        )}
      </div>

      <div className="space-y-2 mb-5">
        {FEATURES.map((feature) => (
          <div key={feature} className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <Check className="w-3 h-3 text-primary" />
            </div>
            <span className="text-sm text-foreground">{feature}</span>
          </div>
        ))}
      </div>

      <BillingToggle selected={selectedPlan} onChange={onPlanChange} />

      <div className="text-center mb-4">
        {isAnnual ? (
          <>
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="text-3xl font-bold text-foreground">$6.40</span>
              <span className="text-muted-foreground text-sm">/ month</span>
              <span className="text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">$76.80 billed annually · Cancel anytime</p>
          </>
        ) : (
          <>
            <div>
              <span className="text-3xl font-bold text-foreground">$8</span>
              <span className="text-muted-foreground text-sm"> / month</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Cancel anytime. No contracts.</p>
          </>
        )}
      </div>

      <Button
        onClick={onCheckout}
        disabled={loading}
        className="w-full h-12 rounded-xl text-base font-semibold"
        style={{
          background: "linear-gradient(135deg, hsl(188 90% 42%) 0%, hsl(205 88% 52%) 100%)",
        }}
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            {isAnnual ? "Start Pro — $6.40/month" : "Start ScribPost Pro"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>

      <div className="text-center mt-3">
        <Link href="/pricing" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <Gift className="w-3.5 h-3.5" />
          Gift a membership to someone else
        </Link>
      </div>
    </motion.div>
  );
}

export function PaywallModal({
  show,
  onDismiss,
  hard = false,
  defaultPlan,
}: {
  show: boolean;
  onDismiss: () => void;
  hard?: boolean;
  defaultPlan?: BillingInterval;
}) {
  const { authToken } = useAppStore();
  const { toast } = useToast();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const [step, setStep] = useState<ModalStep>(authToken ? "plans" : "login");
  const [selectedPlan, setSelectedPlan] = useState<BillingInterval>(defaultPlan ?? "monthly");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      triggerRef.current = document.activeElement;
      setStep(authToken ? "plans" : "login");
      setSelectedPlan(defaultPlan ?? "monthly");
    } else if (triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [show, authToken, defaultPlan]);

  useEffect(() => {
    if (!show) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    requestAnimationFrame(() => {
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => !el.hasAttribute("disabled"));
      if (focusable.length > 0) focusable[0].focus();
    });
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !hard) {
        onDismiss();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => !el.hasAttribute("disabled"));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, hard, onDismiss]);

  const currentStep = authToken && step !== "plans" ? "plans" : step;

  const handleAuthSuccess = ({ isSubscribed: alreadySub }: AuthSuccessPayload) => {
    if (alreadySub) {
      onDismiss();
    } else {
      setStep("plans");
    }
  };

  const handleCheckout = async () => {
    if (!authToken) {
      toast({ title: "Please log in first", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/payments/create-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ plan: selectedPlan }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: data.error || "Failed to start checkout", variant: "destructive" });
        return;
      }

      const { checkoutUrl } = await res.json();
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(5, 8, 18, 0.96)", backdropFilter: "blur(16px)" }}
        >
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="paywall-title"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full max-w-md bg-card border border-border rounded-3xl p-8 shadow-2xl"
          >
            <h2 id="paywall-title" className="sr-only">Upgrade to ScribPost Pro</h2>

            {!hard && (
              <button
                onClick={onDismiss}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors p-1"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            <div className="flex justify-center mb-4">
              <ScribPostLogo />
            </div>

            <AnimatePresence mode="wait">
              {currentStep === "plans" && (
                <PlansStep
                  key="plans"
                  loading={loading}
                  onCheckout={handleCheckout}
                  hard={hard}
                  selectedPlan={selectedPlan}
                  onPlanChange={setSelectedPlan}
                />
              )}

              {currentStep === "signup" && (
                <div key="signup">
                  <InlineSignupForm onSuccess={handleAuthSuccess} />
                  <p className="text-center text-xs text-muted-foreground mt-4">
                    Already have an account?{" "}
                    <button
                      onClick={() => setStep("login")}
                      className="text-primary hover:underline font-medium"
                    >
                      Log in
                    </button>
                  </p>
                </div>
              )}

              {currentStep === "login" && (
                <div key="login">
                  <InlineLoginForm onSuccess={handleAuthSuccess} />
                  <p className="text-center text-xs text-muted-foreground mt-4">
                    Don't have an account?{" "}
                    <button
                      onClick={() => setStep("signup")}
                      className="text-primary hover:underline font-medium"
                    >
                      Sign up
                    </button>
                  </p>
                </div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
